import CustomCheckbox from 'components/CustomCheckbox';
import TextSubHeader from 'components/CustomText/TextSubHeader';
import TipsIcon from 'components/TipsIcon';
import usePrevious from 'hooks/usePrevious';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useQuery from 'hooks/useQuery';
import { SET_COMMENT, GET_COMMENT } from 'store/key';
import styled from 'styled-components/macro';
import { addRemoveRequest, deleteRemoveRequest } from 'utility/general';
import CommentIcon from 'components/CommentBox/CommentIcon';
import {
    APPLICANT,
    CCO,
    CLUSTER_ADMIN,
    DRAFT,
    IRB,
    RETURNED,
    SECRETARY,
    APPROVED,
    ACKNOWLEDGED,
    DISAPPROVED,
    REVIEWER,
    FOR_REVIEW,
    COMPLETED,
    REVIEWED,
    TERMINATED,
    SUBMITTED,
    RE_SUBMITTED,
    DISTRIBUTED,
} from 'utility/lookup';

const SLayout = styled.div`
    display: flex;
    min-height: 25px;
`;

const SNumberLayout = styled.div`
    width: ${(props) => (props.level - 1) * 18 + 35}px;
    flex-shrink: 0;
    justify-content: flex-end;
    padding-left: ${(props) => `${(props.level - 2) * 18 + 12 * (props.level - 1)}px`};
    line-height: 1.5;
`;

const SContentLayout = styled.div`
    flex-grow: 1;
`;

const NumberListFormItem = ({
    number = '',
    level = 1,
    children,
    label = '',
    displayContentNextLine = false,
    disableVerticalCenter = false,
    disableOnPermission = false,
    style = {},
    bold = false,
    italic = false,
    color = '',
    alignItems = 'center',
    showTrueFalse = false,
    trueFalseValue,
    onTrueFalseSelected = (boolean) => null,
    labelTooltip = '',
    name = '',
    fontSize = 0,
    allowAction = true,
    onAppFieldValidate,
    disableCheckBox = false,
    showDisableMessage = false,
    updateApplicationFormOnDisableChange = -1,
    updateSiteFormOnDisableChange = -1, // site-info id
    test = false,
    highlight = false,
    labelWidth,
    isEnableComment = true, 
    commentProps = {},
    ...rest
}) => {
    const formId = useQuery('id');
    const applicationDetail = useSelector((state) => state.applicationDetail[formId ?? 'undefined']);
    
    const extraProps = {};
    const previousDisable = usePrevious(`${allowAction}`);

    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    
    const isEnableAddComment = useMemo(() => {
        return isEnableComment && commentProps?.commentLabel && !commentProps?.commentStatus && 
                !commentProps?.commentDetail && user?.role !== APPLICANT;
    }, [isEnableComment, commentProps, user])

    const addComment = () => {
        if (!isEnableAddComment) return;
        dispatch({
            type: SET_COMMENT,
            payload: {
                addBox: {
                    isShow: true,
                    label: commentProps?.commentLabel,
                    questionNo: number,
                    questionField: commentProps?.commentField,
                }
            },
        });
    }

    const onOpenCommentBox = () => {

        console.log('open comment clicked 2');
        dispatch({ type: GET_COMMENT, payload: commentProps?.commentDetail?.id })

    }

    useEffect(() => {
        // updateApplicationFormOnDisableChange = application id
        if (previousDisable && (updateApplicationFormOnDisableChange >= 0 || updateSiteFormOnDisableChange >= 0)) {
            if (!name) {
                throw new Error('name is required');
            }

            const isDisabled = getDisableExceptionPermission();
            if (isDisabled) {
                addRemoveRequest({
                    applicationFormId: updateApplicationFormOnDisableChange,
                    name: name,
                    siteInfoId: updateSiteFormOnDisableChange,
                });
            } else {
                deleteRemoveRequest({
                    applicationFormId: updateApplicationFormOnDisableChange,
                    keys: [name],
                    siteInfoId: updateSiteFormOnDisableChange,
                });
            }
        }

        checkAppFieldValidate();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowAction, disableCheckBox]);

    if (!disableVerticalCenter) {
        extraProps.style = { alignItems: alignItems };
    }

    useEffect(() => {
        checkAppFieldValidate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trueFalseValue]);

    const checkAppFieldValidate = () => {
        if (onAppFieldValidate !== undefined) {
            if (!name) {
                throw new Error('name is required');
            }

            const isDisabled = getDisableExceptionPermission();

            if (isDisabled) {
                onAppFieldValidate(name, true);
            } else {
                if (trueFalseValue === true || trueFalseValue === false) {
                    onAppFieldValidate(name, true);
                } else {
                    onAppFieldValidate(name, false);
                }
            }
        }
    };

    const [decisionValues] = useState([
        { key: 'yes', value: true, display: 'Yes' },
        { key: 'no', value: true, display: 'No' },
    ]);

    const getDisableExceptionPermission = () => {
        if (disableCheckBox) {
            return true;
        }

        if (allowAction === false) {
            return true;
        }

        return false;
    };

    const getCheckboxDisabled = () => {
        if (disableOnPermission) {
            return true;
        }

        if (disableCheckBox) {
            return true;
        }

        if (allowAction === false) {
            return true;
        }

        return false;
    };

    const isNumber = () => {
        if (number === '-') {
            return false;
        }

        if (!number && number !== 0) {
            return false;
        }

        return true;
    };

    const getTrueFalseValue = () => {
        if (trueFalseValue === true) {
            return 'yes';
        } else if (trueFalseValue === false) {
            return 'no';
        } else {
            return '';
        }
    };

    const getTrueFalseComponent = () => {
        return (
            <CustomCheckbox
                defaultData={decisionValues}
                value={[getTrueFalseValue()]}
                radioButton
                disabled={getCheckboxDisabled()}
                onItemChange={(key, value) => {
                    if (value === true) {
                        const index = decisionValues.findIndex((draftItem) => {
                            return draftItem.key === key;
                        });

                        const decision = index === 0;
                        onTrueFalseSelected({ target: { value: decision, name } });
                    }
                }}
            />
        );
    };

    const extraLabelProps = {};

    if (labelWidth) {
        extraLabelProps.width = labelWidth;
    }

    return (
        <TextSubHeader style={{ marginTop: 12, ...style }} {...rest}>
            <SLayout {...extraProps}>
                <SNumberLayout level={level} onClick={addComment} style={{ cursor: isEnableAddComment ? 'pointer' : 'default' }}>
                    <span
                        style={{
                            background: highlight ? 'yellow' : 'initial',
                            color: showDisableMessage ? '#5ac49f' : number === '28' ? 'grey' : 'inherit',
                        }}
                    >
                        {number}
                        {number && isNumber() && '.'}
                    </span>
                </SNumberLayout>
                {commentProps?.commentDetail && (
                    <CommentIcon commentStatus={commentProps?.commentDetail?.commentStatus} onClick={onOpenCommentBox} />
                )}
                {label && (
                    <div style={{ marginRight: 15, ...extraLabelProps }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <TextSubHeader
                                style={{
                                    fontWeight: bold ? 'bold' : 'normal',
                                    fontStyle: italic ? 'italic' : 'normal',
                                    color: showDisableMessage ? '#5ac49f' : color ? color : 'inherit',
                                    fontSize: fontSize || 'inherit',
                                    lineHeight: 1.5,
                                }}
                            >
                                {label}

                                <span> {labelTooltip && <TipsIcon title={labelTooltip} />}</span>
                                {showDisableMessage && (
                                    <div
                                        style={{ display: 'inline', fontSize: 12, fontWeight: 'bold', marginLeft: 10 }}
                                    >
                                        (*To Complete in Site info)
                                    </div>
                                )}
                            </TextSubHeader>
                        </div>
                    </div>
                )}
                {!displayContentNextLine && (
                    <SContentLayout>{showTrueFalse ? getTrueFalseComponent() : children}</SContentLayout>
                )}
            </SLayout>
            {displayContentNextLine && (
                <div style={{ marginTop: 5, display: 'flex', width: '100%' }}>
                    <SNumberLayout level={level}></SNumberLayout>
                    {showTrueFalse ? getTrueFalseComponent() : children}
                </div>
            )}
        </TextSubHeader>
    );
};

export default NumberListFormItem;
